import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";

const productUrl = (productId) =>
  `/customer/dsok/products/${encodeURIComponent(productId)}`;

const ProductSalesReport = ({ data }) => {
  const { t } = useTranslation();
  const products = Array.isArray(data?.products) ? data.products : [];
  const unmatched = Array.isArray(data?.unmatched) ? data.unmatched : [];
  const totalSold = data?.totalSold ?? 0;
  const ordersCount = data?.ordersCount ?? 0;

  const [search, setSearch] = useState("");
  const [soldOnly, setSoldOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const rows = useMemo(() => {
    const merged = [
      ...products.map((p) => ({ ...p, unmatched: false })),
      ...unmatched.map((u) => ({ ...u, productId: null, unmatched: true })),
    ];
    return merged.sort(
      (a, b) =>
        (b.count || 0) - (a.count || 0) ||
        String(a.name || "").localeCompare(String(b.name || "")),
    );
  }, [products, unmatched]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (soldOnly && !(r.count > 0)) return false;
      if (q && !String(r.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, soldOnly]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const resetPage = () => setPage(0);

  return (
    <Box sx={{ mt: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
      >
        <TextField
          size="small"
          label={t("pages.dsokReports.productSalesSearch", {
            defaultValue: "Hľadať produkt",
          })}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetPage();
          }}
          sx={{ minWidth: 240 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={soldOnly}
              onChange={(e) => {
                setSoldOnly(e.target.checked);
                resetPage();
              }}
            />
          }
          label={t("pages.dsokReports.productSalesSoldOnly", {
            defaultValue: "Len predané",
          })}
        />
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
          <Chip
            variant="outlined"
            label={`${t("pages.dsokReports.productSalesTotalSold", {
              defaultValue: "Predaných spolu",
            })}: ${totalSold}`}
          />
          <Chip
            variant="outlined"
            label={`${t("pages.dsokReports.productSalesOrders", {
              defaultValue: "Objednávok",
            })}: ${ordersCount}`}
          />
          <Chip
            variant="outlined"
            label={`${t("pages.dsokReports.productSalesProducts", {
              defaultValue: "Produktov",
            })}: ${products.length}`}
          />
        </Stack>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("pages.dsokReports.productSalesColName", {
                  defaultValue: "Produkt",
                })}
              </TableCell>
              <TableCell>
                {t("pages.dsokReports.productSalesColId", {
                  defaultValue: "ID produktu",
                })}
              </TableCell>
              <TableCell>
                {t("pages.dsokReports.productSalesColType", {
                  defaultValue: "Typ",
                })}
              </TableCell>
              <TableCell align="right">
                {t("pages.dsokReports.productSalesColCount", {
                  defaultValue: "Počet predajov",
                })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2 }}
                  >
                    {t("pages.dsokReports.productSalesEmpty", {
                      defaultValue: "Žiadne produkty pre zvolený filter.",
                    })}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paged.map((r, idx) => (
                <TableRow key={r.productId || `unmatched-${r.name}-${idx}`}>
                  <TableCell>
                    {r.productId ? (
                      <Link
                        href={productUrl(r.productId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: "inline-flex", alignItems: "center" }}
                      >
                        {r.name}
                        <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5 }} />
                      </Link>
                    ) : (
                      r.name
                    )}
                    {r.unmatched && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ ml: 1 }}
                        label={t("pages.dsokReports.productSalesOutOfCatalog", {
                          defaultValue: "mimo katalógu",
                        })}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {r.productId || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.productType || "—"}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: r.count > 0 ? 600 : 400 }}
                    >
                      {r.count || 0}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage={t("pages.dsokReports.externalOrdersRowsPerPage", {
            defaultValue: "Riadkov na stranu",
          })}
        />
      </TableContainer>
    </Box>
  );
};

export default ProductSalesReport;
